import React, { useState, useEffect, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import jsQR from 'jsqr';
import { PDFDocument } from 'pdf-lib';
import apiClient from '../../../services/apiClient';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns';

const CLASS_MATERIAL_TYPES = ['class_material', 'homework_material', 'exam_material'];

// PDF.js worker 설정 - CDN 사용
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.149/pdf.worker.min.mjs';

const QRBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0 });
  const [studentHomework, setStudentHomework] = useState({}); // {studentId: [{homework}]}
  const [availableStudents, setAvailableStudents] = useState([]); // 사용자의 모든 학생 목록
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [pagePreview, setPagePreview] = useState([]); // 미리보기 이미지 URL 배열
  const [showPreview, setShowPreview] = useState(false); // 미리보기 모달 표시 여부
  const [previewLoading, setPreviewLoading] = useState(false); // 미리보기 로딩 상태
  const [bulkComment, setBulkComment] = useState(''); // 일괄 적용 코멘트 (독립적인 state)
  const [bulkClassContent, setBulkClassContent] = useState([]); // 수업 내용 일괄 적용
  const [bulkHomeworkItems, setBulkHomeworkItems] = useState([]); // 과제 일괄 적용
  const [forceSend, setForceSend] = useState(false); // 대기열 무시하고 즉시 발송 여부
  const [classDate, setClassDate] = useState(() => new Date()); // 수업 날짜 (현재 시간)
  const [classTime, setClassTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }); // 수업 시간 (현재 시간)

  const BRAND_COLOR = '#11346A';
  const ACCENT_COLOR = '#FF4444';

  // 학생 목록 조회
  useEffect(() => {
    if (isOpen) {
      fetchAvailableStudents();
    }
  }, [isOpen]);

  useEffect(() => {
    if (scanResults.length === 0) {
      setActiveResultIndex(0);
      return;
    }

    setActiveResultIndex((prev) => Math.min(prev, scanResults.length - 1));
  }, [scanResults.length]);

  const fetchAvailableStudents = async () => {
    try {
      const response = await apiClient.get('/api/v1/paper-assignment/students/', { withAuth: true });
      setAvailableStudents(response.data || []);
    } catch (error) {
      console.error('학생 목록 조회 실패:', error);
      setAvailableStudents([]);
    }
  };

  const uploadTypes = [
    { value: 'class_material', label: '수업 자료' },
    { value: 'homework_material', label: '과제 자료' },
    { value: 'exam_material', label: '시험 자료' },
    { value: 'student_submission', label: '학생 제출본' },
    { value: 'feedback', label: '첨삭본' }
  ];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 파일 확장자 검증 (MIME 타입이 정확하지 않을 수 있으므로)
      const fileName = file.name.toLowerCase();
      const isPdf = fileName.endsWith('.pdf') || file.type === 'application/pdf';

      if (isPdf) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('PDF 파일만 업로드 가능합니다.');
        setSelectedFile(null);
      }
    } else {
      setError('PDF 파일만 업로드 가능합니다.');
      setSelectedFile(null);
    }
  };

  const handleScan = async () => {
    if (!selectedFile) {
      setError('파일을 선택해주세요.');
      return;
    }

    setScanning(true);
    setScanProgress(0);
    setError('');

    try {
      // PDF 로드
      const arrayBuffer = await selectedFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdf.numPages;

      const results = [];

      // 각 페이지 스캔
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        setScanProgress(Math.round((pageNum / totalPages) * 100));

        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2.0 });

        // Canvas 생성
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // 페이지 렌더링
        await page.render({ canvasContext: context, viewport }).promise;

        // 이미지 데이터 추출
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

        // QR 코드 스캔
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert'
        });

        if (qrCode) {
          try {
            const qrData = JSON.parse(qrCode.data);
            results.push({
              pageNum,
              studentId: qrData.student_id,
              studentName: qrData.student_name,
              levelId: qrData.level_id,
              className: qrData.class_name,
              userId: qrData.user_id,
              hasQR: true
            });
          } catch (error) {
            console.error(`페이지 ${pageNum} QR 파싱 오류:`, error);
            results.push({
              pageNum,
              hasQR: false,
              error: 'QR 데이터 파싱 실패'
            });
          }
        } else {
          // QR 코드 없음 - 이전 페이지의 학생에게 속함
          if (results.length > 0 && results[results.length - 1].hasQR) {
            results.push({
              ...results[results.length - 1],
              pageNum,  // 스프레드 이후에 pageNum 설정하여 덮어씌우기 방지
              hasQR: false,  // QR이 없는 페이지이므로 false로 설정
              continuesFrom: results[results.length - 1].pageNum
            });
          } else {
            results.push({
              pageNum,
              hasQR: false,
              error: 'QR 코드 없음'
            });
          }
        }
      }

      // 학생별 그룹핑
      const grouped = groupPagesByStudent(results);
      // 각 그룹에 기본 uploadType, attendance, attitude 추가
      const groupedWithType = grouped.map(group => ({
        ...group,
        uploadType: '', // 기본값 비어있음
        attendance: null, // 기본값: 선택 안 함
        attitude: null, // 기본값: 선택 안 함
        classComment: '',
        scoreOptional: false, // 점수 입력 선택사항 여부
        classContent: [], // 수업 내용 항목
        homeworkItems: [] // 과제 항목
      }));
      setScanResults(groupedWithType);
      const firstManualIndex = groupedWithType.findIndex(item => item.needsManualAssignment);
      setActiveResultIndex(firstManualIndex !== -1 ? firstManualIndex : 0);

    } catch (error) {
      console.error('PDF 스캔 오류:', error);
      setError('PDF 스캔 중 오류가 발생했습니다.');
    } finally {
      setScanning(false);
    }
  };

  const groupPagesByStudent = (results) => {
    const groups = [];
    let currentGroup = null;

    results.forEach((result) => {
      if (result.hasQR && !result.continuesFrom) {
        // 새로운 학생 시작 (QR 코드가 있는 첫 페이지)
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          studentId: result.studentId,
          studentName: result.studentName,
          levelId: result.levelId,
          className: result.className,
          userId: result.userId,
          pages: []  // QR 표지 페이지는 제외, 빈 배열로 시작
        };
      } else if (currentGroup && (result.continuesFrom || result.hasQR === false)) {
        // 현재 학생의 추가 페이지
        // continuesFrom이 있거나, QR이 없는 모든 페이지는 현재 그룹에 추가
        currentGroup.pages.push(result.pageNum);
      } else if (!result.hasQR && !currentGroup) {
        // QR 없는 페이지인데 그룹이 없음 - 맨 앞에 QR 없이 시작
        groups.push({
          studentId: null,
          studentName: 'QR 인식 실패',
          pages: [result.pageNum],
          error: result.error,
          needsManualAssignment: true
        });
      }
    });

    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  };

  const fetchStudentHomework = async (studentId, type = 'student_submission') => {
    try {
      const response = await apiClient.get(`/api/v1/paper-assignment/student-assignments/?student_id=${studentId}`);

      // 타입에 따라 다른 필터링
      const allHomework = response.data.flatMap(assignment =>
        assignment.homework_files
          .filter(hw => {
            if (type === 'student_submission') {
              // 학생 제출본: 아직 제출 안 된 것만
              return !hw.student_submission;
            } else if (type === 'feedback') {
              // 첨삭본: 제출은 됐지만 첨삭 안 된 것만 (feedback_file_name이 없는 것)
              return hw.student_submission && !hw.student_submission.feedback_file_name;
            }
            return false;
          })
          .map(hw => ({
            ...hw,
            assignment_date: assignment.created_at,
            level_name: assignment.level_name
          }))
      );

      setStudentHomework(prev => ({ ...prev, [studentId]: allHomework }));
    } catch (error) {
      console.error('과제 조회 실패:', error);
      setStudentHomework(prev => ({ ...prev, [studentId]: [] }));
    }
  };

  const handleUploadTypeChange = async (type) => {
    // 모든 학생의 과제 목록 조회 (student_submission이나 feedback인 경우)
    if (type === 'student_submission' || type === 'feedback') {
      const uniqueStudentIds = [...new Set(scanResults.filter(r => !r.needsManualAssignment).map(r => r.studentId))];

      await Promise.all(
        uniqueStudentIds.map(studentId => fetchStudentHomework(studentId, type))
      );
    }

    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        uploadType: type,
        selectedHomeworkId: null,
        feedbackScore: null,
        isNewHomework: false,
        homeworkTitle: '',
        score: null,
        totalScore: 100,
        classComment: CLASS_MATERIAL_TYPES.includes(type) ? (result.classComment || '') : '',
        scoreOptional: false, // 타입 변경 시 점수 선택사항 초기화
        classContent: CLASS_MATERIAL_TYPES.includes(type) ? (result.classContent || []) : [],
        homeworkItems: CLASS_MATERIAL_TYPES.includes(type) ? (result.homeworkItems || []) : []
      }
    ));
  };

  const handleAttendanceChange = (index, attendance) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, attendance } : result
    ));
  };

  const handleAttitudeChange = (index, attitude) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, attitude } : result
    ));
  };

  const handleClassCommentChange = (index, comment) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, classComment: comment } : result
    ));
  };

  const handleManualStudentAssignment = (index, studentId) => {
    if (!studentId) return;

    const selectedStudent = availableStudents.find(s => s.id === parseInt(studentId));
    if (!selectedStudent) return;

    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? {
        ...result,
        studentId: selectedStudent.id,
        studentName: selectedStudent.name,
        levelId: selectedStudent.level,
        className: selectedStudent.level_name,
        userId: selectedStudent.user,
        needsManualAssignment: false, // 수동 지정 완료
        uploadType: '', // 타입은 다시 선택하도록
        attendance: null,
        attitude: null,
        classComment: '',
        classContent: [],
        homeworkItems: []
      } : result
    ));
  };

  const handleBulkUploadTypeChange = async (type) => {
    await handleUploadTypeChange(type);
  };

  const handleBulkAttendanceChange = (attendance) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, attendance }
    ));
  };

  const handleBulkAttitudeChange = (attitude) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, attitude }
    ));
  };

  const handleBulkClassCommentChange = (comment) => {
    setBulkComment(comment);
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, classComment: comment }
    ));
  };

  const handleFeedbackScoreChange = (index, score) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, feedbackScore: score } : result
    ));
  };

  const handleBulkNewHomeworkToggle = (isNew) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, isNewHomework: isNew, selectedHomeworkId: null }
    ));
  };

  const handleBulkHomeworkTitleChange = (title) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, homeworkTitle: title }
    ));
  };

  const handleBulkHomeworkSelect = (homeworkId) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, selectedHomeworkId: homeworkId }
    ));
  };

  const handleScoreChange = (index, score) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, score: parseInt(score) || null } : result
    ));
  };

  const handleTotalScoreChange = (index, totalScore) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, totalScore: parseInt(totalScore) || 100 } : result
    ));
  };

  const handleScoreOptionalChange = (index, optional) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? { ...result, scoreOptional: optional, score: optional ? null : result.score, totalScore: optional ? null : (result.totalScore || 100) } : result
    ));
  };

  const handleBulkScoreOptionalChange = (optional) => {
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : { ...result, scoreOptional: optional, score: optional ? null : result.score, totalScore: optional ? null : (result.totalScore || 100) }
    ));
  };

  // Bulk Class Content Handlers
  const addBulkClassContentItem = () => {
    const newItem = {
      id: Date.now(),
      type: 'lesson',
      title: '',
      score: '',
      totalScore: 100,
      scoreOptional: false,
      file: null,
      source: 'bulk'
    };
    setBulkClassContent(prev => [...prev, newItem]);
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        classContent: [...(result.classContent || []), newItem]
      }
    ));
  };

  const removeBulkClassContentItem = (id) => {
    setBulkClassContent(prev => prev.filter(item => item.id !== id));
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        classContent: (result.classContent || []).filter(item => item.id !== id)
      }
    ));
  };

  const updateBulkClassContentItem = (id, field, value) => {
    setBulkClassContent(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        classContent: (result.classContent || []).map(item =>
          item.id === id ? { ...item, [field]: value } : item
        )
      }
    ));
  };

  // Bulk Homework Handlers
  const addBulkHomeworkItem = () => {
    const newId = bulkHomeworkItems.length > 0
      ? Math.max(...bulkHomeworkItems.map(item => item.id)) + 1
      : 1;
    const newItem = {
      id: newId,
      file: null,
      description: '',
      hasEndDate: false,
      endDate: '',
      source: 'bulk'
    };
    setBulkHomeworkItems(prev => [...prev, newItem]);
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        homeworkItems: [...(result.homeworkItems || []), newItem]
      }
    ));
  };

  const removeBulkHomeworkItem = (itemId) => {
    setBulkHomeworkItems(prev => prev.filter(item => item.id !== itemId));
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        homeworkItems: (result.homeworkItems || []).filter(item => item.id !== itemId)
      }
    ));
  };

  const updateBulkHomeworkItem = (itemId, field, value) => {
    setBulkHomeworkItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));
    setScanResults(prev => prev.map(result =>
      result.needsManualAssignment ? result : {
        ...result,
        homeworkItems: (result.homeworkItems || []).map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      }
    ));
  };

  // Individual Student Class Content Handlers
  const addStudentClassContentItem = (index) => {
    const newItem = {
      id: Date.now(),
      type: 'lesson',
      title: '',
      score: '',
      totalScore: 100,
      scoreOptional: false,
      file: null,
      source: 'individual'
    };
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? {
        ...result,
        classContent: [...(result.classContent || []), newItem]
      } : result
    ));
  };

  const removeStudentClassContentItem = (index, itemId) => {
    setScanResults(prev => prev.map((result, idx) => {
      if (idx !== index) return result;
      const itemToRemove = (result.classContent || []).find(item => item.id === itemId);
      if (itemToRemove && itemToRemove.source === 'individual') {
        return {
          ...result,
          classContent: result.classContent.filter(item => item.id !== itemId)
        };
      }
      return result;
    }));
  };

  const updateStudentClassContentItem = (index, id, field, value) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? {
        ...result,
        classContent: (result.classContent || []).map(item =>
          item.id === id ? { ...item, [field]: value } : item
        )
      } : result
    ));
  };

  // Individual Student Homework Handlers
  const addStudentHomeworkItem = (index) => {
    const currentItems = scanResults[index]?.homeworkItems || [];
    const newId = currentItems.length > 0
      ? Math.max(...currentItems.map(item => item.id)) + 1
      : 1;
    const newItem = {
      id: newId,
      file: null,
      description: '',
      hasEndDate: false,
      endDate: '',
      source: 'individual'
    };
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? {
        ...result,
        homeworkItems: [...(result.homeworkItems || []), newItem]
      } : result
    ));
  };

  const removeStudentHomeworkItem = (index, itemId) => {
    setScanResults(prev => prev.map((result, idx) => {
      if (idx !== index) return result;
      const itemToRemove = (result.homeworkItems || []).find(item => item.id === itemId);
      if (itemToRemove && itemToRemove.source === 'individual') {
        return {
          ...result,
          homeworkItems: result.homeworkItems.filter(item => item.id !== itemId)
        };
      }
      return result;
    }));
  };

  const updateStudentHomeworkItem = (index, itemId, field, value) => {
    setScanResults(prev => prev.map((result, idx) =>
      idx === index ? {
        ...result,
        homeworkItems: (result.homeworkItems || []).map(item =>
          item.id === itemId ? { ...item, [field]: value } : item
        )
      } : result
    ));
  };

  const handleActiveResultSelect = (value) => {
    if (!scanResults.length) {
      setActiveResultIndex(0);
      return;
    }

    const nextIndex = typeof value === 'number' ? value : parseInt(value, 10);
    if (Number.isNaN(nextIndex)) {
      return;
    }

    const boundedIndex = Math.min(Math.max(nextIndex, 0), scanResults.length - 1);
    setActiveResultIndex(boundedIndex);
  };

  const handlePreviewPages = async (pages) => {
    if (!selectedFile || !pages || pages.length === 0) return;

    setPreviewLoading(true);
    setShowPreview(true);
    setPagePreview([]);

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const previews = [];

      // 모든 페이지를 렌더링
      for (const pageNum of pages) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.5 });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const imageUrl = canvas.toDataURL('image/png');
        previews.push({ pageNum, imageUrl });
      }

      setPagePreview(previews);
    } catch (error) {
      console.error('페이지 미리보기 오류:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const goToPreviousResult = () => {
    setActiveResultIndex(prev => Math.max(prev - 1, 0));
  };

  const goToNextResult = () => {
    setActiveResultIndex(prev => (scanResults.length ? Math.min(prev + 1, scanResults.length - 1) : 0));
  };

  const summaryStats = useMemo(() => {
    const total = scanResults.length;
    const manual = scanResults.filter(result => result.needsManualAssignment).length;
    const pendingType = scanResults.filter(result => !result.needsManualAssignment && !result.uploadType).length;
    const classMaterialSelections = scanResults.filter(result => CLASS_MATERIAL_TYPES.includes(result.uploadType)).length;
    const completed = scanResults.filter(result => !result.needsManualAssignment && !!result.uploadType).length;

    return {
      total,
      manual,
      pendingType,
      classMaterialSelections,
      completed,
    };
  }, [scanResults]);

  const activeResult = scanResults[activeResultIndex] || null;

  const firstAssignableResult = useMemo(
    () => scanResults.find(result => !result.needsManualAssignment) || null,
    [scanResults]
  );

  const attendanceBulkState = useMemo(() => {
    const eligible = scanResults.filter(result => !result.needsManualAssignment && CLASS_MATERIAL_TYPES.includes(result.uploadType));
    if (!eligible.length) {
      return { allTrue: false, allFalse: false, allNull: false };
    }

    return {
      allTrue: eligible.every(result => result.attendance === true),
      allFalse: eligible.every(result => result.attendance === false),
      allNull: eligible.every(result => result.attendance === null),
    };
  }, [scanResults]);

  const attitudeBulkState = useMemo(() => {
    const eligible = scanResults.filter(result => !result.needsManualAssignment && CLASS_MATERIAL_TYPES.includes(result.uploadType));
    if (!eligible.length) {
      return { activeValue: null };
    }

    const attitudes = [null, '상', '중', '하'];
    const matched = attitudes.find(attitude => eligible.every(result => result.attitude === attitude));
    return { activeValue: matched !== undefined ? matched : null };
  }, [scanResults]);

  const sharedHomeworkOptions = useMemo(() => {
    if (!scanResults.length) return [];
    const allHomework = Object.values(studentHomework).flat();
    return allHomework.filter((hw, index, self) => index === self.findIndex(item => item.id === hw.id));
  }, [studentHomework, scanResults]);

  const bulkUploadType = firstAssignableResult?.uploadType || '';
  const isSubmissionUploadType = ['student_submission', 'feedback'].includes(bulkUploadType);
  const isBulkNewHomework = firstAssignableResult?.isNewHomework || false;
  const bulkSelectedHomeworkId = firstAssignableResult?.selectedHomeworkId || '';
  const bulkHomeworkTitle = firstAssignableResult?.homeworkTitle || '';

  const splitPdfByPages = async (pdfFile, pageNumbers, result) => {
    try {
      // 원본 PDF 로드
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      // 새 PDF 생성
      const newPdf = await PDFDocument.create();

      // 지정된 페이지들을 복사 (페이지 번호는 1부터 시작하므로 -1)
      for (const pageNum of pageNumbers) {
        const [copiedPage] = await newPdf.copyPages(pdfDoc, [pageNum - 1]);
        newPdf.addPage(copiedPage);
      }

      // PDF를 Blob으로 변환
      const pdfBytes = await newPdf.save();
      return new Blob([pdfBytes], { type: 'application/pdf' });
    } catch (error) {
      console.error('PDF 분할 오류:', error);
      throw error;
    }
  };

  const submitClassContent = async (result, pdfBlob) => {
    try {
      const formData = new FormData();
      formData.append('level_id', result.levelId);
      formData.append('student_id', result.studentId);

      // Always append attendance and attitude (even if null)
      formData.append('attendance', result.attendance);
      formData.append('attitude', result.attitude);

      formData.append('comment', result.classComment || '');

      // class_content 타입에 따라 다른 type 설정
      let contentType = '';
      if (result.uploadType === 'class_material') {
        contentType = 'lesson';
      } else if (result.uploadType === 'homework_material') {
        contentType = 'homework';
      } else if (result.uploadType === 'exam_material') {
        contentType = 'exam';
      }

      // Primary QR PDF as first class content item
      const primaryItem = {
        id: Date.now(),
        type: contentType,
        title: `${result.studentName}`,
        score: result.scoreOptional ? null : (result.score || null),
        totalScore: result.scoreOptional ? null : (result.totalScore || null),
        comment: result.classComment || ''
      };

      // Merge additional class content items from bulk + individual
      const additionalItems = (result.classContent || []).map(item => ({
        id: item.id,
        type: item.type,
        title: item.title,
        score: item.scoreOptional ? null : (item.score || null),
        totalScore: item.scoreOptional ? null : (item.totalScore || null)
      }));

      const allClassContent = [primaryItem, ...additionalItems];
      formData.append('class_content', JSON.stringify(allClassContent));

      // Primary PDF file
      const fileName = `qr_upload_${result.studentId}_${contentType}_${Date.now()}.pdf`;
      formData.append('class_content_files', pdfBlob, fileName);

      // Append additional class content files
      (result.classContent || []).forEach(item => {
        if (item.file) {
          formData.append('class_content_files', item.file);
        }
      });

      // Append homework items from bulk + individual
      const allHomeworkItems = (result.homeworkItems || []);
      allHomeworkItems.forEach(item => {
        if (item.file || (item.description && item.description.trim())) {
          if (item.file) {
            formData.append('homework_files', item.file);
          }
          formData.append('homework_titles', item.description || item.file?.name || '제목 없음');

          let endDateValue = '';
          if (item.hasEndDate && item.endDate) {
            const date = new Date(item.endDate);
            endDateValue = date.toISOString().split('T')[0];
          }
          formData.append('homework_end_dates', endDateValue);
        }
      });

      // Append force_send flag
      formData.append('force_send', forceSend);

      // Append class_date in ISO format with time (local time converted to UTC)
      const formattedDate = classDate.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss in UTC
      formData.append('class_date', formattedDate);

      console.log('📤 제출 데이터:', {
        studentId: result.studentId,
        studentName: result.studentName,
        levelId: result.levelId,
        contentType,
        fileName,
        blobSize: pdfBlob.size,
        classContentCount: allClassContent.length,
        homeworkItemCount: allHomeworkItems.length
      });

      const response = await apiClient.post('/api/v1/paper-assignment/create-class-notice/', formData, {
        withAuth: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('✅ 제출 성공:', response.data);
      return { success: true };
    } catch (error) {
      console.error('❌ Class content 제출 오류:', error);
      console.error('Error response:', error.response?.data);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const submitStudentSubmission = async (result, pdfBlob) => {
    try {
      if (result.isNewHomework) {
        // 새 과제 생성 후 학생 제출본 업로드
        const formData = new FormData();
        formData.append('level_id', result.levelId);
        formData.append('student_id', result.studentId);
        // Don't set attendance/attitude for student submissions
        formData.append('comment', '');

        // homework_files로 과제 생성
        const fileName = `new_homework_${result.studentId}_${Date.now()}.pdf`;
        formData.append('homework_files', pdfBlob, fileName);
        formData.append('homework_titles', result.homeworkTitle);

        // Append force_send flag
        formData.append('force_send', forceSend);

        // Append class_date in ISO format with time (local time converted to UTC)
        const formattedDate = classDate.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss in UTC
        formData.append('class_date', formattedDate);

        console.log('📤 새 과제 생성 + 학생 제출본:', {
          studentId: result.studentId,
          studentName: result.studentName,
          homeworkTitle: result.homeworkTitle,
          score: result.score,
          totalScore: result.totalScore,
          fileName,
          blobSize: pdfBlob.size
        });

        // 과제 생성 API 호출 (homework를 PaperAssignment에 첨부)
        const response = await apiClient.post('/api/v1/paper-assignment/create-class-notice/', formData, {
          withAuth: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // 생성된 homework의 ID 가져오기
        const createdHomeworkId = response.data.paper_assignment.homework_files[0]?.id;

        if (!createdHomeworkId) {
          throw new Error('과제 생성 실패: homework ID를 가져올 수 없습니다.');
        }

        // 이제 해당 homework에 학생 제출본 업로드
        const submissionFormData = new FormData();
        submissionFormData.append('homework_id', createdHomeworkId);
        submissionFormData.append('student_id', result.studentId);

        const submissionFileName = `student_submission_${result.studentId}_hw${createdHomeworkId}_${Date.now()}.pdf`;
        submissionFormData.append('solution_file', pdfBlob, submissionFileName);

        await apiClient.post('/api/v1/paper-assignment/submit-solution/', submissionFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('✅ 새 과제 + 학생 제출본 업로드 성공');
        return { success: true };

      } else {
        // 기존 과제에 학생 제출본 업로드
        if (!result.selectedHomeworkId) {
          return { success: false, error: '과제를 선택해주세요.' };
        }

        const formData = new FormData();
        formData.append('homework_id', result.selectedHomeworkId);
        formData.append('student_id', result.studentId);

        const fileName = `student_submission_${result.studentId}_hw${result.selectedHomeworkId}_${Date.now()}.pdf`;
        formData.append('solution_file', pdfBlob, fileName);

        console.log('📤 학생 제출본 제출:', {
          studentId: result.studentId,
          studentName: result.studentName,
          homeworkId: result.selectedHomeworkId,
          fileName,
          blobSize: pdfBlob.size
        });

        const response = await apiClient.post('/api/v1/paper-assignment/submit-solution/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('✅ 학생 제출본 업로드 성공:', response.data);
        return { success: true };
      }
    } catch (error) {
      console.error('❌ 학생 제출본 제출 오류:', error);
      console.error('Error response:', error.response?.data);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const submitFeedback = async (result, pdfBlob) => {
    try {
      if (result.isNewHomework) {
        // 새 과제 생성 + 학생 제출본 업로드 + 첨삭본 업로드
        const formData = new FormData();
        formData.append('level_id', result.levelId);
        formData.append('student_id', result.studentId);
        // Don't set attendance/attitude for feedback uploads
        formData.append('comment', '');

        // homework_files로 과제 생성
        const fileName = `new_homework_feedback_${result.studentId}_${Date.now()}.pdf`;
        formData.append('homework_files', pdfBlob, fileName);
        formData.append('homework_titles', result.homeworkTitle);

        // Append class_date in ISO format with time (local time converted to UTC)
        const formattedDate = classDate.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss in UTC
        formData.append('class_date', formattedDate);

        console.log('📤 새 과제 생성 + 첨삭본:', {
          studentId: result.studentId,
          studentName: result.studentName,
          homeworkTitle: result.homeworkTitle,
          score: result.score,
          totalScore: result.totalScore,
          feedbackScore: result.feedbackScore,
          fileName,
          blobSize: pdfBlob.size
        });

        // 과제 생성 API 호출
        const response = await apiClient.post('/api/v1/paper-assignment/create-class-notice/', formData, {
          withAuth: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // 생성된 homework의 ID 가져오기
        const createdHomeworkId = response.data.paper_assignment.homework_files[0]?.id;

        if (!createdHomeworkId) {
          throw new Error('과제 생성 실패: homework ID를 가져올 수 없습니다.');
        }

        // 학생 제출본 업로드 (빈 파일 또는 동일 파일)
        const submissionFormData = new FormData();
        submissionFormData.append('homework_id', createdHomeworkId);
        submissionFormData.append('student_id', result.studentId);
        submissionFormData.append('solution_file', pdfBlob, `submission_${Date.now()}.pdf`);

        const submissionResponse = await apiClient.post('/api/v1/paper-assignment/submit-solution/', submissionFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const submissionId = submissionResponse.data.submission.id;

        // 첨삭본 업로드
        const gradeFormData = new FormData();
        gradeFormData.append('submission_id', submissionId);
        gradeFormData.append('score', result.feedbackScore);
        gradeFormData.append('feedback_file', pdfBlob, `feedback_${Date.now()}.pdf`);

        await apiClient.post('/api/v1/paper-assignment/grade-submission/', gradeFormData, {
          withAuth: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('✅ 새 과제 + 첨삭본 업로드 성공');
        return { success: true };

      } else {
        // 기존 과제에 첨삭본 업로드
        if (!result.selectedHomeworkId) {
          return { success: false, error: '과제를 선택해주세요.' };
        }

        if (!result.feedbackScore) {
          return { success: false, error: '점수를 입력해주세요.' };
        }

        // 먼저 해당 homework의 submission을 찾아야 함
        const homeworkData = studentHomework[result.studentId]?.find(hw => hw.id === result.selectedHomeworkId);

        if (!homeworkData || !homeworkData.student_submission) {
          return { success: false, error: '학생이 아직 과제를 제출하지 않았습니다.' };
        }

        const submissionId = homeworkData.student_submission.id;

        const formData = new FormData();
        formData.append('submission_id', submissionId);
        formData.append('score', result.feedbackScore);

        const fileName = `feedback_${result.studentId}_hw${result.selectedHomeworkId}_${Date.now()}.pdf`;
        formData.append('feedback_file', pdfBlob, fileName);

        console.log('📤 첨삭본 제출:', {
          studentId: result.studentId,
          studentName: result.studentName,
          homeworkId: result.selectedHomeworkId,
          submissionId,
          score: result.feedbackScore,
          fileName,
          blobSize: pdfBlob.size
        });

        const response = await apiClient.post('/api/v1/paper-assignment/grade-submission/', formData, {
          withAuth: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        console.log('✅ 첨삭본 업로드 성공:', response.data);
        return { success: true };
      }
    } catch (error) {
      console.error('❌ 첨삭본 제출 오류:', error);
      console.error('Error response:', error.response?.data);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const handleSubmit = async () => {
    // 수동 지정이 필요한 페이지가 있는지 확인
    const needsManualAssignmentResults = scanResults.filter(r => r.needsManualAssignment);
    if (needsManualAssignmentResults.length > 0) {
      setError('수동 지정이 필요한 페이지가 있습니다. 학생을 선택해주세요.');
      return;
    }

    // 모든 그룹에 타입이 선택되었는지 확인
    const validResults = scanResults.filter(r => !r.needsManualAssignment);
    const missingTypes = validResults.filter(r => !r.uploadType);

    if (missingTypes.length > 0) {
      setError('모든 학생의 업로드 타입을 선택해주세요.');
      return;
    }

    // student_submission이나 feedback 타입 검증
    const submissionFeedbackErrors = validResults.filter(r => {
      if (r.uploadType === 'student_submission' || r.uploadType === 'feedback') {
        if (r.isNewHomework) {
          // 새 과제: 제목과 점수 필요
          if (!r.homeworkTitle || !r.homeworkTitle.trim()) return true;
          if (r.score === null || r.score === undefined) return true;
          if (r.totalScore === null || r.totalScore === undefined || r.totalScore <= 0) return true;
        } else {
          // 기존 과제 선택: selectedHomeworkId 필요
          if (!r.selectedHomeworkId) return true;
        }

        // feedback는 추가로 feedbackScore 필요
        if (r.uploadType === 'feedback') {
          if (!r.feedbackScore || r.feedbackScore < 0 || r.feedbackScore > r.totalScore) return true;
        }
      }
      return false;
    });

    if (submissionFeedbackErrors.length > 0) {
      setError('학생 제출본/첨삭본: 과제 선택 또는 새 과제 정보(제목, 점수)를 입력해주세요.');
      return;
    }

    // 과제/시험 자료 타입은 점수 필요 (scoreOptional이 false인 경우만)
    const materialScoreErrors = validResults.filter(r => {
      if (r.uploadType === 'homework_material' || r.uploadType === 'exam_material') {
        // scoreOptional이 true면 점수 검증 스킵
        if (r.scoreOptional) return false;

        if (r.score === null || r.score === undefined) return true;
        if (r.totalScore === null || r.totalScore === undefined || r.totalScore <= 0) return true;
      }
      return false;
    });

    if (materialScoreErrors.length > 0) {
      setError('과제/시험 자료는 점수(얻은점수/총점)를 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setSubmitProgress({ current: 0, total: validResults.length });
    setError('');

    const results = [];

    try {
      for (let i = 0; i < validResults.length; i++) {
        const result = validResults[i];
        setSubmitProgress({ current: i + 1, total: validResults.length });

        // PDF 분할 (QR 표지 페이지 포함)
        const pdfBlob = await splitPdfByPages(selectedFile, result.pages, result);

        // 타입에 따라 다른 처리
        let submitResult;
        if (['class_material', 'homework_material', 'exam_material'].includes(result.uploadType)) {
          submitResult = await submitClassContent(result, pdfBlob);
        } else if (result.uploadType === 'student_submission') {
          submitResult = await submitStudentSubmission(result, pdfBlob);
        } else if (result.uploadType === 'feedback') {
          submitResult = await submitFeedback(result, pdfBlob);
        }

        results.push({
          student: result.studentName,
          success: submitResult.success,
          error: submitResult.error
        });
      }

      // 결과 확인
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (failCount > 0) {
        const failedStudents = results.filter(r => !r.success).map(r => r.student).join(', ');
        setError(`${failCount}명 제출 실패: ${failedStudents}`);
      } else {
        alert(`${successCount}명의 과제가 성공적으로 제출되었습니다!`);
        if (onSuccess) onSuccess();
        handleClose();
      }

    } catch (error) {
      console.error('제출 오류:', error);
      setError('제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setScanning(false);
    setScanProgress(0);
    setScanResults([]);
    setError('');
    setForceSend(false);
    setBulkClassContent([]);
    setBulkHomeworkItems([]);
    setBulkComment('');
    const resetDate = new Date();
    setClassDate(resetDate);
    setClassTime(`${String(resetDate.getHours()).padStart(2, '0')}:${String(resetDate.getMinutes()).padStart(2, '0')}`);
    onClose();
  };
  const renderScanResultsSection = () => {
    if (!scanResults.length) {
      return null;
    }

    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid #EBEBEB', paddingBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '18px' }}>스캔 결과</h3>
            <span style={{ fontSize: '13px', color: '#666' }}>
              준비 완료 {summaryStats.completed} / {summaryStats.total}
            </span>
          </div>

          <div style={{ border: '1px solid #EBEBEB', borderRadius: '8px', backgroundColor: '#f8fbff', padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>총 그룹</span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: BRAND_COLOR }}>{summaryStats.total}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>학생 지정 필요</span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: summaryStats.manual ? ACCENT_COLOR : BRAND_COLOR }}>{summaryStats.manual}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>타입 미선택</span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: summaryStats.pendingType ? '#b45309' : BRAND_COLOR }}>{summaryStats.pendingType}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>수업 자료/시험 자료</span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: '#2563eb' }}>{summaryStats.classMaterialSelections}</span>
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: 600, color: '#333' }}>학생별 요약</span>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>학생을 선택하면 오른쪽 상세 설정이 바뀝니다.</span>
            </div>
            <div style={{ border: '1px solid #EBEBEB', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1fr', gap: '12px', padding: '10px 16px', backgroundColor: '#f3f4f6', fontSize: '12px', color: '#475569', fontWeight: 600 }}>
                <span>학생</span>
                <span>페이지</span>
                <span>업로드 타입</span>
                <span style={{ textAlign: 'right' }}>상태</span>
              </div>
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {scanResults.map((result, idx) => {
                  const isActive = idx === activeResultIndex;
                  const typeLabel = uploadTypes.find(type => type.value === result.uploadType)?.label || '타입 미선택';
                  const statusLabel = result.needsManualAssignment
                    ? '학생 지정 필요'
                    : result.uploadType
                      ? '준비 완료'
                      : '타입 선택 필요';
                  const statusStyle = result.needsManualAssignment
                    ? { backgroundColor: '#fee2e2', color: '#b91c1c' }
                    : result.uploadType
                      ? { backgroundColor: '#dcfce7', color: '#15803d' }
                      : { backgroundColor: '#fef3c7', color: '#92400e' };

                  return (
                    <div
                      key={`${result.studentId || 'manual'}-${idx}`}
                      onClick={() => handleActiveResultSelect(idx)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleActiveResultSelect(idx);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1.2fr 1fr',
                        gap: '12px',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: isActive ? '#e8f4ff' : '#fff',
                        borderBottom: idx === scanResults.length - 1 ? 'none' : '1px solid #EBEBEB',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '14px', color: isActive ? BRAND_COLOR : '#111827', fontWeight: isActive ? 600 : 500 }}>
                          {result.studentName || '학생 미지정'}
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                          {result.className || '반 정보 없음'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#475569' }}>{result.pages.join(', ')}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewPages(result.pages);
                          }}
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#f8fafc',
                            color: '#475569',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          미리보기
                        </button>
                      </div>
                      <span style={{ fontSize: '12px', color: '#1d4ed8' }}>{typeLabel}</span>
                      <span
                        style={{
                          justifySelf: 'end',
                          padding: '4px 10px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: statusStyle.backgroundColor,
                          color: statusStyle.color
                        }}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#333' }}>
              상세 학생 선택 ({activeResultIndex + 1}/{summaryStats.total})
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                onClick={goToPreviousResult}
                disabled={activeResultIndex === 0}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: activeResultIndex === 0 ? '#f3f4f6' : '#fff',
                  color: '#4b5563',
                  cursor: activeResultIndex === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                이전
              </button>
              <select
                value={String(activeResultIndex)}
                onChange={(e) => handleActiveResultSelect(e.target.value)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  color: '#111827',
                  outline: 'none',
                  backgroundColor: '#fff'
                }}
              >
                {scanResults.map((result, idx) => (
                  <option key={`${result.studentId || 'manual'}-${idx}`} value={idx}>
                    {`${idx + 1}. ${result.studentName || '학생 미지정'}${result.className ? ` (${result.className})` : ''}`}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={goToNextResult}
                disabled={activeResultIndex === scanResults.length - 1}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: activeResultIndex === scanResults.length - 1 ? '#f3f4f6' : '#fff',
                  color: '#4b5563',
                  cursor: activeResultIndex === scanResults.length - 1 ? 'not-allowed' : 'pointer'
                }}
              >
                다음
              </button>
            </div>
          </div>

          <div style={{ border: '1px solid #EBEBEB', borderRadius: '8px', backgroundColor: '#fff', padding: '18px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ fontWeight: 600, color: '#333' }}>전체 설정</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>업로드 타입</label>
                <select
                  value={bulkUploadType}
                  onChange={(e) => handleBulkUploadTypeChange(e.target.value)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">타입 선택...</option>
                  {uploadTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>출석 일괄 적용 <span style={{ fontSize: '12px', color: '#999' }}>(선택사항)</span></span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { label: '선택 안 함', value: null },
                      { label: '모두 출석', value: true },
                      { label: '모두 결석', value: false }
                    ].map(option => {
                      const isHighlighted = option.value === null
                        ? attendanceBulkState.allNull
                        : option.value
                          ? attendanceBulkState.allTrue
                          : attendanceBulkState.allFalse;
                      return (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => handleBulkAttendanceChange(option.value)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '6px',
                            border: `1px solid ${isHighlighted ? BRAND_COLOR : '#d1d5db'}`,
                            backgroundColor: isHighlighted ? BRAND_COLOR : '#fff',
                            color: isHighlighted ? '#fff' : '#475569',
                            fontSize: '13px',
                            fontWeight: isHighlighted ? 600 : 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>태도 일괄 적용 <span style={{ fontSize: '12px', color: '#999' }}>(선택사항)</span></span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[
                      { label: '선택 안 함', value: null },
                      { label: '모두 상', value: '상' },
                      { label: '모두 중', value: '중' },
                      { label: '모두 하', value: '하' }
                    ].map(option => {
                      const isHighlighted = attitudeBulkState.activeValue === option.value;
                      return (
                        <button
                          key={option.label}
                          type="button"
                          onClick={() => handleBulkAttitudeChange(option.value)}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '6px',
                            border: `1px solid ${isHighlighted ? BRAND_COLOR : '#d1d5db'}`,
                            backgroundColor: isHighlighted ? BRAND_COLOR : '#fff',
                            color: isHighlighted ? '#fff' : '#475569',
                            fontSize: '13px',
                            fontWeight: isHighlighted ? 600 : 500,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {(bulkUploadType === 'homework_material' || bulkUploadType === 'exam_material') && (
                <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>점수 입력 <span style={{ fontSize: '12px', color: '#999' }}>(선택사항)</span></span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff' }}>
                    <input
                      type="checkbox"
                      checked={firstAssignableResult?.scoreOptional || false}
                      onChange={(e) => handleBulkScoreOptionalChange(e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#374151' }}>점수 입력하지 않기 (체크 시 점수 필드 숨김)</span>
                  </label>
                </div>
              )}

              {(bulkUploadType === 'class_material' || bulkUploadType === 'homework_material' || bulkUploadType === 'exam_material') && (
                <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>코멘트 일괄 적용 <span style={{ fontSize: '12px', color: '#999' }}>(선택사항)</span></span>
                  <textarea
                    value={bulkComment}
                    onChange={(e) => handleBulkClassCommentChange(e.target.value)}
                    placeholder='전체 학생에게 적용할 코멘트를 입력하세요'
                    rows={3}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'vertical',
                      backgroundColor: '#fff',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Bulk Class Content - gated by CLASS_MATERIAL_TYPES */}
            {CLASS_MATERIAL_TYPES.includes(bulkUploadType) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>수업 내용 일괄 적용 (시험/과제 결과)</span>
                  <button
                    type="button"
                    onClick={addBulkClassContentItem}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    + 항목 추가
                  </button>
                </div>

                {bulkClassContent.length === 0 ? (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    border: '2px dashed #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    fontSize: '13px'
                  }}>
                    수업 내용이나 시험/과제 결과를 추가해보세요
                  </div>
                ) : (
                  <div style={{
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                    overflow: 'hidden'
                  }}>
                    {bulkClassContent.map((item, index) => (
                      <div key={item.id} style={{
                        padding: '14px',
                        borderBottom: index < bulkClassContent.length - 1 ? '1px solid #f1f3f4' : 'none',
                        backgroundColor: '#fafbfc'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <div style={{
                            minWidth: '22px',
                            height: '22px',
                            backgroundColor: item.type === 'lesson' ? '#17a2b8' :
                                           item.type === 'exam' ? '#dc3545' : '#28a745',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            {item.type === 'lesson' ? '📖' : item.type === 'exam' ? '📋' : '📝'}
                          </div>

                          <select
                            value={item.type}
                            onChange={(e) => updateBulkClassContentItem(item.id, 'type', e.target.value)}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '500',
                              backgroundColor: '#fff',
                              color: '#495057',
                              minWidth: '70px'
                            }}
                          >
                            <option value="lesson">수업</option>
                            <option value="exam">시험</option>
                            <option value="homework">과제</option>
                          </select>

                          <input
                            type="text"
                            placeholder="제목을 입력하세요"
                            value={item.title}
                            onChange={(e) => updateBulkClassContentItem(item.id, 'title', e.target.value)}
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              border: '1px solid #dee2e6',
                              borderRadius: '6px',
                              fontSize: '13px',
                              backgroundColor: '#fff'
                            }}
                          />

                          <button
                            type="button"
                            onClick={() => removeBulkClassContentItem(item.id)}
                            style={{
                              padding: '5px 8px',
                              fontSize: '11px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            삭제
                          </button>
                        </div>

                        {(item.type === 'exam' || item.type === 'homework') && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                            marginLeft: '32px',
                            padding: '10px',
                            backgroundColor: '#fff',
                            border: '1px solid #e9ecef',
                            borderRadius: '6px'
                          }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={item.scoreOptional || false}
                                onChange={(e) => updateBulkClassContentItem(item.id, 'scoreOptional', e.target.checked)}
                                style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                              />
                              <span style={{ fontSize: '12px', color: '#495057' }}>점수 입력하지 않기</span>
                            </label>

                            {!item.scoreOptional && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <label style={{ fontSize: '13px', color: '#495057', fontWeight: '500', minWidth: '35px' }}>
                                  점수:
                                </label>
                                <input
                                  type="number"
                                  placeholder="0"
                                  value={item.score}
                                  onChange={(e) => updateBulkClassContentItem(item.id, 'score', e.target.value)}
                                  style={{
                                    width: '60px',
                                    padding: '5px 8px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                  }}
                                />
                                <span style={{ color: '#6c757d', fontSize: '13px' }}>/</span>
                                <input
                                  type="number"
                                  placeholder="100"
                                  value={item.totalScore || ''}
                                  onChange={(e) => updateBulkClassContentItem(item.id, 'totalScore', e.target.value)}
                                  style={{
                                    width: '60px',
                                    padding: '5px 8px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    textAlign: 'center'
                                  }}
                                />
                                <span style={{ color: '#6c757d', fontSize: '13px' }}>점</span>
                                {item.score && item.totalScore && (
                                  <div style={{
                                    marginLeft: '8px',
                                    padding: '3px 6px',
                                    backgroundColor: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#d4edda' :
                                                   parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#fff3cd' : '#f8d7da',
                                    color: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#155724' :
                                          parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#856404' : '#721c24',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: '500'
                                  }}>
                                    {Math.round((parseFloat(item.score) / parseFloat(item.totalScore)) * 100)}%
                                  </div>
                                )}
                              </div>
                            )}

                            {item.scoreOptional && (
                              <span style={{ fontSize: '11px', color: '#6c757d', fontStyle: 'italic' }}>
                                점수를 입력하지 않습니다
                              </span>
                            )}
                          </div>
                        )}

                        {/* File attachment */}
                        <div style={{
                          marginLeft: '32px',
                          marginTop: '8px',
                          padding: '10px',
                          backgroundColor: '#fff',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px'
                        }}>
                          <label style={{ fontSize: '13px', color: '#495057', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                            파일 첨부 (선택):
                          </label>
                          <input
                            type="file"
                            onChange={(e) => updateBulkClassContentItem(item.id, 'file', e.target.files[0])}
                            style={{
                              fontSize: '12px',
                              padding: '4px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              width: '100%',
                              backgroundColor: '#f8f9fa'
                            }}
                          />
                          {item.file && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: '#28a745' }}>
                              ✓ {item.file.name}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bulk Homework - gated by CLASS_MATERIAL_TYPES */}
            {CLASS_MATERIAL_TYPES.includes(bulkUploadType) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>과제 일괄 적용</span>
                  <button
                    type="button"
                    onClick={addBulkHomeworkItem}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      borderRadius: '5px',
                      backgroundColor: '#28a745',
                      border: 'none',
                      color: '#FFF',
                      cursor: 'pointer'
                    }}
                  >
                    + 과제 추가
                  </button>
                </div>

                {bulkHomeworkItems.length === 0 ? (
                  <div style={{
                    padding: '20px',
                    border: '1px dashed #EBEBEB',
                    borderRadius: '5px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '13px'
                  }}>
                    과제가 없습니다. "+ 과제 추가" 버튼을 클릭하여 과제를 추가하세요.
                  </div>
                ) : (
                  bulkHomeworkItems.map((item, index) => (
                    <div key={item.id} style={{
                      padding: '14px',
                      border: '1px solid #EBEBEB',
                      borderRadius: '5px',
                      backgroundColor: '#f9f9f9'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: '500', color: '#333', fontSize: '13px' }}>과제 {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeBulkHomeworkItem(item.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            borderRadius: '3px',
                            backgroundColor: '#dc3545',
                            border: 'none',
                            color: '#FFF',
                            cursor: 'pointer'
                          }}
                        >
                          삭제
                        </button>
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                          과제 제목
                        </label>
                        <input
                          type="text"
                          placeholder="과제 제목을 입력하세요"
                          value={item.description}
                          onChange={(e) => updateBulkHomeworkItem(item.id, 'description', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '7px',
                            border: '1px solid #EBEBEB',
                            borderRadius: '5px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                            backgroundColor: '#FFF'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                          과제 파일 선택 <span style={{ fontSize: '11px', color: '#999' }}>(선택사항)</span>
                        </label>
                        <input
                          type="file"
                          onChange={(e) => updateBulkHomeworkItem(item.id, 'file', e.target.files[0])}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: '1px solid #EBEBEB',
                            borderRadius: '5px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                            backgroundColor: '#FFF'
                          }}
                        />
                        {item.file && (
                          <div style={{ marginTop: '4px', color: '#666', fontSize: '11px' }}>
                            선택된 파일: {item.file.name}
                          </div>
                        )}
                      </div>

                      {/* End date section */}
                      <div style={{
                        padding: '10px',
                        backgroundColor: '#f0f8ff',
                        border: '1px solid #d0e8ff',
                        borderRadius: '5px'
                      }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          marginBottom: '6px'
                        }}>
                          <input
                            type="checkbox"
                            checked={item.hasEndDate || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              updateBulkHomeworkItem(item.id, 'hasEndDate', checked);
                              if (checked && !item.endDate) {
                                const defaultDate = new Date();
                                defaultDate.setDate(defaultDate.getDate() + 3);
                                const formatted = defaultDate.toISOString().slice(0, 10);
                                updateBulkHomeworkItem(item.id, 'endDate', formatted);
                              }
                            }}
                            style={{
                              marginRight: '6px',
                              cursor: 'pointer',
                              width: '14px',
                              height: '14px'
                            }}
                          />
                          <span style={{ fontSize: '13px', color: '#333', fontWeight: '500' }}>
                            제출 마감일 설정
                          </span>
                        </label>

                        {item.hasEndDate && (
                          <div style={{ marginTop: '6px' }}>
                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                              마감일시
                            </label>
                            <input
                              type="date"
                              value={item.endDate || ''}
                              onChange={(e) => updateBulkHomeworkItem(item.id, 'endDate', e.target.value)}
                              style={{
                                width: '100%',
                                padding: '7px',
                                border: '1px solid #d0e8ff',
                                borderRadius: '5px',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                                backgroundColor: '#FFF'
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {isSubmissionUploadType && firstAssignableResult && (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontWeight: 500, color: '#1f2937', fontSize: '13px' }}>과제 연결</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="radio"
                      name="bulk-homework-type"
                      checked={!isBulkNewHomework}
                      onChange={() => handleBulkNewHomeworkToggle(false)}
                      style={{ cursor: 'pointer' }}
                    />
                    기존 과제
                  </label>
                  <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="radio"
                      name="bulk-homework-type"
                      checked={!!isBulkNewHomework}
                      onChange={() => handleBulkNewHomeworkToggle(true)}
                      style={{ cursor: 'pointer' }}
                    />
                    새 과제
                  </label>
                </div>

                {isBulkNewHomework ? (
                  <input
                    type="text"
                    value={bulkHomeworkTitle}
                    onChange={(e) => handleBulkHomeworkTitleChange(e.target.value)}
                    placeholder="과제 제목을 입력하세요"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: bulkHomeworkTitle ? '#fff' : '#fff8e1'
                    }}
                  />
                ) : sharedHomeworkOptions.length > 0 ? (
                  <select
                    value={bulkSelectedHomeworkId}
                    onChange={(e) => handleBulkHomeworkSelect(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: bulkSelectedHomeworkId ? '#fff' : '#fff8e1',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="">과제 선택...</option>
                    {sharedHomeworkOptions.map(hw => (
                      <option key={hw.id} value={hw.id}>
                        {hw.title} ({new Date(hw.assignment_date).toLocaleDateString('ko-KR')})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                    연결 가능한 과제가 없습니다.
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ border: '1px solid #EBEBEB', borderRadius: '8px', backgroundColor: '#fff', padding: '20px', boxShadow: '0 8px 20px rgba(17, 52, 106, 0.08)' }}>
            {!activeResult ? (
              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                상세 설정할 학생이 없습니다.
              </div>
            ) : (() => {
              const typeLabel = uploadTypes.find(type => type.value === activeResult.uploadType)?.label || '타입 미선택';
              const isClassMaterial = CLASS_MATERIAL_TYPES.includes(activeResult.uploadType);
              const requiresScore = !activeResult.needsManualAssignment && (
                (activeResult.uploadType === 'homework_material' || activeResult.uploadType === 'exam_material') ||
                (['student_submission', 'feedback'].includes(activeResult.uploadType) && activeResult.isNewHomework)
              );
              const requiresFeedbackScore = !activeResult.needsManualAssignment && activeResult.uploadType === 'feedback' && !activeResult.isNewHomework;
              const statusLabel = activeResult.needsManualAssignment
                ? '학생 지정 필요'
                : activeResult.uploadType
                  ? '준비 완료'
                  : '타입 선택 필요';
              const statusChipStyle = activeResult.needsManualAssignment
                ? { backgroundColor: '#fee2e2', color: '#b91c1c' }
                : activeResult.uploadType
                  ? { backgroundColor: '#dcfce7', color: '#15803d' }
                  : { backgroundColor: '#fef3c7', color: '#92400e' };

              const scoreControl = (() => {
                if (activeResult.needsManualAssignment) {
                  return <span style={{ color: '#9ca3af', fontSize: '13px' }}>학생 지정 후 입력 가능합니다.</span>;
                }

                if (requiresScore) {
                  const showScoreCheckbox = (activeResult.uploadType === 'homework_material' || activeResult.uploadType === 'exam_material');

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {showScoreCheckbox && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={activeResult.scoreOptional || false}
                            onChange={(e) => handleScoreOptionalChange(activeResultIndex, e.target.checked)}
                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                          />
                          <span style={{ fontSize: '12px', color: '#64748b' }}>점수 입력하지 않기</span>
                        </label>
                      )}
                      {!activeResult.scoreOptional && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type='number'
                            min='0'
                            value={activeResult.score || ''}
                            onChange={(e) => handleScoreChange(activeResultIndex, e.target.value)}
                            placeholder='점수'
                            style={{
                              flex: 1,
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              outline: 'none',
                              backgroundColor: activeResult.score ? '#fff' : '#fff8e1',
                              textAlign: 'center'
                            }}
                          />
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>/</span>
                          <input
                            type='number'
                            min='1'
                            value={activeResult.totalScore || 100}
                            onChange={(e) => handleTotalScoreChange(activeResultIndex, e.target.value)}
                            placeholder='100'
                            style={{
                              flex: 1,
                              padding: '8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              fontSize: '14px',
                              outline: 'none',
                              textAlign: 'center'
                            }}
                          />
                        </div>
                      )}
                      {activeResult.scoreOptional && (
                        <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>점수를 입력하지 않습니다</span>
                      )}
                    </div>
                  );
                }

                if (requiresFeedbackScore) {
                  return (
                    <input
                      type='number'
                      min='0'
                      max={activeResult.totalScore || 100}
                      value={activeResult.feedbackScore || ''}
                      onChange={(e) => handleFeedbackScoreChange(activeResultIndex, parseInt(e.target.value, 10) || '')}
                      placeholder='0-100'
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: activeResult.feedbackScore ? '#fff' : '#fff8e1',
                        textAlign: 'center'
                      }}
                    />
                  );
                }

                return <span style={{ color: '#9ca3af', fontSize: '13px' }}>해당 없음</span>;
              })();

              const commentControl = (() => {
                if (activeResult.needsManualAssignment) {
                  return <span style={{ color: '#9ca3af', fontSize: '13px' }}>학생 지정 후 입력 가능합니다.</span>;
                }

                if (!activeResult.uploadType) {
                  return <span style={{ color: '#dc2626', fontSize: '13px' }}>업로드 타입을 먼저 선택해주세요.</span>;
                }

                if (isClassMaterial) {
                  return (
                    <textarea
                      value={activeResult.classComment || ''}
                      onChange={(e) => handleClassCommentChange(activeResultIndex, e.target.value)}
                      placeholder='코멘트 (선택)'
                      rows={4}
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        backgroundColor: '#fff'
                      }}
                    />
                  );
                }

                return <span style={{ color: '#9ca3af', fontSize: '13px' }}>수업 자료 유형에서만 입력 가능합니다.</span>;
              })();

              const attendanceControl = (!activeResult.needsManualAssignment && isClassMaterial) ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { label: '선택 안 함', value: null },
                    { label: '출석', value: true },
                    { label: '결석', value: false }
                  ].map(option => {
                    const isActiveOption = activeResult.attendance === option.value;
                    return (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => handleAttendanceChange(activeResultIndex, option.value)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          borderRadius: '6px',
                          border: `1px solid ${isActiveOption ? BRAND_COLOR : '#d1d5db'}`,
                          backgroundColor: isActiveOption ? BRAND_COLOR : '#fff',
                          color: isActiveOption ? '#fff' : '#475569',
                          fontSize: '13px',
                          fontWeight: isActiveOption ? 600 : 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              ) : <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>;

              const attitudeControl = (!activeResult.needsManualAssignment && isClassMaterial) ? (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { label: '선택 안 함', value: null },
                    { label: '상', value: '상' },
                    { label: '중', value: '중' },
                    { label: '하', value: '하' }
                  ].map(option => {
                    const isActiveOption = activeResult.attitude === option.value;
                    return (
                      <button
                        key={option.label}
                        type="button"
                        onClick={() => handleAttitudeChange(activeResultIndex, option.value)}
                        style={{
                          flex: 1,
                          padding: '10px',
                          borderRadius: '6px',
                          border: `1px solid ${isActiveOption ? BRAND_COLOR : '#d1d5db'}`,
                          backgroundColor: isActiveOption ? BRAND_COLOR : '#fff',
                          color: isActiveOption ? '#fff' : '#475569',
                          fontSize: '13px',
                          fontWeight: isActiveOption ? 600 : 500,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              ) : <span style={{ color: '#9ca3af', fontSize: '13px' }}>-</span>;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                        {activeResult.studentName || '학생 미지정'}
                      </span>
                      <span style={{ fontSize: '14px', color: '#475569' }}>
                        {activeResult.className || '반 정보 없음'}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                          페이지 {activeResult.pages.join(', ')}
                        </span>
                        <button
                          type="button"
                          onClick={() => handlePreviewPages(activeResult.pages)}
                          style={{
                            padding: '3px 10px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            border: '1px solid #cbd5e1',
                            backgroundColor: '#f8fafc',
                            color: '#475569',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          미리보기
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                      <span
                        style={{
                          padding: '4px 12px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: statusChipStyle.backgroundColor,
                          color: statusChipStyle.color
                        }}
                      >
                        {statusLabel}
                      </span>
                      <span
                        style={{
                          padding: '6px 14px',
                          borderRadius: '999px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: activeResult.uploadType ? '#eff6ff' : '#fef2f2',
                          color: activeResult.uploadType ? '#1d4ed8' : '#b91c1c'
                        }}
                      >
                        {typeLabel}
                      </span>
                    </div>
                  </div>

                  {activeResult.needsManualAssignment && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>학생 지정</label>
                      <select
                        value={activeResult.studentId || ''}
                        onChange={(e) => handleManualStudentAssignment(activeResultIndex, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          border: `1px solid ${ACCENT_COLOR}`,
                          backgroundColor: '#fff8f8',
                          fontSize: '14px',
                          outline: 'none',
                          color: ACCENT_COLOR
                        }}
                      >
                        <option value=''>학생 선택</option>
                        {availableStudents.map((student) => (
                          <option key={student.id} value={student.id}>
                            {student.name} ({student.level_name})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>점수</span>
                      {scoreControl}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', gridColumn: '1 / -1' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>코멘트</span>
                      {commentControl}
                    </div>
                  </div>

                  <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '50%', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>출석</span>
                      {attendanceControl}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '50%', gap: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>태도</span>
                      {attitudeControl}
                    </div>
                  </div>

                  {/* Individual Class Content */}
                  {isClassMaterial && !activeResult.needsManualAssignment && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                          수업 내용 ({(activeResult.classContent || []).length}개)
                        </span>
                        <button
                          type="button"
                          onClick={() => addStudentClassContentItem(activeResultIndex)}
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          + 항목 추가 (개별)
                        </button>
                      </div>

                      {(activeResult.classContent || []).length === 0 ? (
                        <div style={{
                          padding: '16px',
                          textAlign: 'center',
                          border: '2px dashed #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d',
                          fontSize: '12px'
                        }}>
                          일괄 설정에서 추가하거나, "항목 추가" 버튼으로 개별 수업 내용을 추가하세요
                        </div>
                      ) : (
                        <div style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          overflow: 'hidden'
                        }}>
                          {(activeResult.classContent || []).map((item, index) => (
                            <div key={item.id} style={{
                              padding: '12px',
                              borderBottom: index < activeResult.classContent.length - 1 ? '1px solid #f1f3f4' : 'none',
                              backgroundColor: item.source === 'bulk' ? '#f0f8ff' : '#fafbfc'
                            }}>
                              {/* Source badge */}
                              {item.source === 'bulk' && (
                                <div style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  marginBottom: '6px'
                                }}>
                                  일괄 설정
                                </div>
                              )}
                              {item.source === 'individual' && (
                                <div style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#388e3c',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  marginBottom: '6px'
                                }}>
                                  개별 설정
                                </div>
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <div style={{
                                  minWidth: '20px',
                                  height: '20px',
                                  backgroundColor: item.type === 'lesson' ? '#17a2b8' :
                                                 item.type === 'exam' ? '#dc3545' : '#28a745',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontSize: '10px',
                                  fontWeight: 'bold'
                                }}>
                                  {item.type === 'lesson' ? '📖' : item.type === 'exam' ? '📋' : '📝'}
                                </div>

                                <select
                                  value={item.type}
                                  onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'type', e.target.value)}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    padding: '5px 8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#fff',
                                    color: '#495057',
                                    minWidth: '65px',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  <option value="lesson">수업</option>
                                  <option value="exam">시험</option>
                                  <option value="homework">과제</option>
                                </select>

                                <input
                                  type="text"
                                  placeholder="제목을 입력하세요"
                                  value={item.title}
                                  onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'title', e.target.value)}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    flex: 1,
                                    padding: '5px 8px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#fff',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                  }}
                                />

                                {item.source === 'individual' && (
                                  <button
                                    type="button"
                                    onClick={() => removeStudentClassContentItem(activeResultIndex, item.id)}
                                    style={{
                                      padding: '4px 8px',
                                      fontSize: '11px',
                                      backgroundColor: '#dc3545',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: '500'
                                    }}
                                  >
                                    삭제
                                  </button>
                                )}
                              </div>

                              {/* Score section */}
                              {(item.type === 'exam' || item.type === 'homework') && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '6px',
                                  marginLeft: '28px',
                                  padding: '8px',
                                  backgroundColor: '#fff',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '6px'
                                }}>
                                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={item.scoreOptional || false}
                                      onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'scoreOptional', e.target.checked)}
                                      disabled={item.source === 'bulk'}
                                      style={{ cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer', width: '13px', height: '13px' }}
                                    />
                                    <span style={{ fontSize: '11px', color: '#495057' }}>점수 입력하지 않기</span>
                                  </label>

                                  {!item.scoreOptional && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <label style={{ fontSize: '12px', color: '#495057', fontWeight: '500', minWidth: '30px' }}>
                                        점수:
                                      </label>
                                      <input
                                        type="number"
                                        placeholder="0"
                                        value={item.score}
                                        onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'score', e.target.value)}
                                        disabled={item.source === 'bulk'}
                                        style={{
                                          width: '55px',
                                          padding: '4px 6px',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          textAlign: 'center',
                                          backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#fff',
                                          cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                        }}
                                      />
                                      <span style={{ color: '#6c757d', fontSize: '12px' }}>/</span>
                                      <input
                                        type="number"
                                        placeholder="100"
                                        value={item.totalScore || ''}
                                        onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'totalScore', e.target.value)}
                                        disabled={item.source === 'bulk'}
                                        style={{
                                          width: '55px',
                                          padding: '4px 6px',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          textAlign: 'center',
                                          backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#fff',
                                          cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                        }}
                                      />
                                      <span style={{ color: '#6c757d', fontSize: '12px' }}>점</span>
                                      {item.score && item.totalScore && (
                                        <div style={{
                                          marginLeft: '8px',
                                          padding: '2px 6px',
                                          backgroundColor: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#d4edda' :
                                                         parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#fff3cd' : '#f8d7da',
                                          color: parseFloat(item.score) / parseFloat(item.totalScore) >= 0.8 ? '#155724' :
                                                parseFloat(item.score) / parseFloat(item.totalScore) >= 0.6 ? '#856404' : '#721c24',
                                          borderRadius: '4px',
                                          fontSize: '10px',
                                          fontWeight: '500'
                                        }}>
                                          {Math.round((parseFloat(item.score) / parseFloat(item.totalScore)) * 100)}%
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* File attachment */}
                              <div style={{
                                marginLeft: '28px',
                                marginTop: '6px',
                                padding: '8px',
                                backgroundColor: '#fff',
                                border: '1px solid #e9ecef',
                                borderRadius: '6px'
                              }}>
                                <label style={{ fontSize: '12px', color: '#495057', fontWeight: '500', display: 'block', marginBottom: '4px' }}>
                                  파일 첨부 (선택):
                                </label>
                                <input
                                  type="file"
                                  onChange={(e) => updateStudentClassContentItem(activeResultIndex, item.id, 'file', e.target.files[0])}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    fontSize: '11px',
                                    padding: '4px',
                                    border: '1px solid #ced4da',
                                    borderRadius: '4px',
                                    width: '100%',
                                    backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#f8f9fa',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                  }}
                                />
                                {item.file && (
                                  <div style={{ marginTop: '4px', fontSize: '10px', color: '#28a745' }}>
                                    ✓ {item.file.name}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Individual Homework */}
                  {isClassMaterial && !activeResult.needsManualAssignment && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>
                          과제 ({(activeResult.homeworkItems || []).length}개)
                        </span>
                        <button
                          type="button"
                          onClick={() => addStudentHomeworkItem(activeResultIndex)}
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            borderRadius: '5px',
                            backgroundColor: '#28a745',
                            border: 'none',
                            color: '#FFF',
                            cursor: 'pointer'
                          }}
                        >
                          + 과제 추가 (개별)
                        </button>
                      </div>

                      {(activeResult.homeworkItems || []).length === 0 ? (
                        <div style={{
                          padding: '16px',
                          border: '1px dashed #EBEBEB',
                          borderRadius: '5px',
                          textAlign: 'center',
                          color: '#999',
                          fontSize: '12px'
                        }}>
                          일괄 설정에서 추가하거나, "+ 과제 추가" 버튼으로 개별 과제를 추가하세요
                        </div>
                      ) : (
                        (activeResult.homeworkItems || []).map((item, index) => (
                          <div key={item.id} style={{
                            padding: '12px',
                            border: '1px solid #EBEBEB',
                            borderRadius: '5px',
                            backgroundColor: item.source === 'bulk' ? '#f0f8ff' : '#f9f9f9'
                          }}>
                            {/* Source badge */}
                            <div style={{ marginBottom: '8px' }}>
                              {item.source === 'bulk' ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}>
                                  일괄 설정
                                </span>
                              ) : (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 6px',
                                  backgroundColor: '#e8f5e9',
                                  color: '#388e3c',
                                  borderRadius: '12px',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}>
                                  개별 설정
                                </span>
                              )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <span style={{ fontWeight: '500', color: '#333', fontSize: '12px' }}>과제 {index + 1}</span>
                              {item.source === 'individual' && (
                                <button
                                  type="button"
                                  onClick={() => removeStudentHomeworkItem(activeResultIndex, item.id)}
                                  style={{
                                    padding: '3px 6px',
                                    fontSize: '10px',
                                    borderRadius: '3px',
                                    backgroundColor: '#dc3545',
                                    border: 'none',
                                    color: '#FFF',
                                    cursor: 'pointer'
                                  }}
                                >
                                  삭제
                                </button>
                              )}
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#666' }}>
                                과제 제목
                              </label>
                              <input
                                type="text"
                                placeholder="과제 제목을 입력하세요"
                                value={item.description}
                                onChange={(e) => updateStudentHomeworkItem(activeResultIndex, item.id, 'description', e.target.value)}
                                disabled={item.source === 'bulk'}
                                style={{
                                  width: '100%',
                                  padding: '6px',
                                  border: '1px solid #EBEBEB',
                                  borderRadius: '5px',
                                  fontSize: '12px',
                                  boxSizing: 'border-box',
                                  backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#FFF',
                                  cursor: item.source === 'bulk' ? 'not-allowed' : 'text'
                                }}
                              />
                            </div>

                            <div style={{ marginBottom: '8px' }}>
                              <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#666' }}>
                                과제 파일 선택 <span style={{ fontSize: '10px', color: '#999' }}>(선택사항)</span>
                              </label>
                              <input
                                type="file"
                                onChange={(e) => updateStudentHomeworkItem(activeResultIndex, item.id, 'file', e.target.files[0])}
                                disabled={item.source === 'bulk'}
                                style={{
                                  width: '100%',
                                  padding: '4px',
                                  border: '1px solid #EBEBEB',
                                  borderRadius: '5px',
                                  fontSize: '11px',
                                  boxSizing: 'border-box',
                                  backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#FFF',
                                  cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                }}
                              />
                              {item.file && (
                                <div style={{ marginTop: '3px', color: '#666', fontSize: '10px' }}>
                                  선택된 파일: {item.file.name}
                                </div>
                              )}
                            </div>

                            {/* End date */}
                            <div style={{
                              padding: '8px',
                              backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#f0f8ff',
                              border: '1px solid #d0e8ff',
                              borderRadius: '5px'
                            }}>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer',
                                marginBottom: '4px'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={item.hasEndDate || false}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    updateStudentHomeworkItem(activeResultIndex, item.id, 'hasEndDate', checked);
                                    if (checked && !item.endDate) {
                                      const defaultDate = new Date();
                                      defaultDate.setDate(defaultDate.getDate() + 3);
                                      const formatted = defaultDate.toISOString().slice(0, 10);
                                      updateStudentHomeworkItem(activeResultIndex, item.id, 'endDate', formatted);
                                    }
                                  }}
                                  disabled={item.source === 'bulk'}
                                  style={{
                                    marginRight: '6px',
                                    cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer',
                                    width: '13px',
                                    height: '13px'
                                  }}
                                />
                                <span style={{ fontSize: '12px', color: '#333', fontWeight: '500' }}>
                                  제출 마감일 설정
                                </span>
                              </label>

                              {item.hasEndDate && (
                                <div style={{ marginTop: '4px' }}>
                                  <label style={{ display: 'block', marginBottom: '3px', fontSize: '11px', color: '#666' }}>
                                    마감일시
                                  </label>
                                  <input
                                    type="date"
                                    value={item.endDate || ''}
                                    onChange={(e) => updateStudentHomeworkItem(activeResultIndex, item.id, 'endDate', e.target.value)}
                                    disabled={item.source === 'bulk'}
                                    style={{
                                      width: '100%',
                                      padding: '5px',
                                      border: '1px solid #d0e8ff',
                                      borderRadius: '5px',
                                      fontSize: '12px',
                                      boxSizing: 'border-box',
                                      backgroundColor: item.source === 'bulk' ? '#f5f5f5' : '#FFF',
                                      cursor: item.source === 'bulk' ? 'not-allowed' : 'pointer'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </>
    );
  };


  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* 미리보기 모달 */}
      {showPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: '20px'
          }}
          onClick={() => setShowPreview(false)}
        >
          <div
            style={{
              position: 'relative',
              width: '90vw',
              maxWidth: '1200px',
              maxHeight: '90vh',
              backgroundColor: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: BRAND_COLOR,
              color: '#fff'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                페이지 미리보기 ({pagePreview.length}페이지)
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* 로딩 상태 */}
            {previewLoading && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#64748b'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #e5e7eb',
                  borderTop: `4px solid ${BRAND_COLOR}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }}></div>
                페이지를 불러오는 중...
              </div>
            )}

            {/* 페이지 갤러리 */}
            {!previewLoading && pagePreview.length > 0 && (
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                backgroundColor: '#f9fafb'
              }}>
                {pagePreview.map((preview, idx) => (
                  <div
                    key={preview.pageNum}
                    style={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      padding: '16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <div style={{
                      marginBottom: '12px',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{
                        backgroundColor: BRAND_COLOR,
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: '999px',
                        fontSize: '12px'
                      }}>
                        페이지 {preview.pageNum}
                      </span>
                    </div>
                    <img
                      src={preview.imageUrl}
                      alt={`페이지 ${preview.pageNum}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb'
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 메인 모달 */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
      <div style={{
        backgroundColor: '#FFF',
        borderRadius: '12px',
        padding: '30px',
        width: '800px',
        maxWidth: '90vw',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px'
        }}>
          <h2 style={{
            margin: 0,
            color: BRAND_COLOR,
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            QR 코드 포함 파일 업로드
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#999',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>

        {/* 파일 선택 */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
            PDF 파일 선택 *
          </label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '15px',
              boxSizing: 'border-box'
            }}
          />
          {selectedFile && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
              선택된 파일: {selectedFile.name}
            </div>
          )}
        </div>

        {/* 수업 날짜 선택 */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
            수업 날짜 📅
          </label>
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            backgroundColor: '#f9fafb'
          }}>
            <style>{`
              .qr-date-picker-modal .rdp {
                margin: 0;
                --rdp-cell-size: 42px;
                --rdp-accent-color: #11346A;
                --rdp-background-color: #e8f0ff;
                font-family: inherit;
              }
              .qr-date-picker-modal .rdp-months {
                justify-content: center;
              }
              .qr-date-picker-modal .rdp-month {
                width: 100%;
              }
              .qr-date-picker-modal .rdp-caption {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0 0 16px 0;
                margin-bottom: 12px;
                border-bottom: 1px solid #e5e7eb;
              }
              .qr-date-picker-modal .rdp-caption_label {
                font-size: 16px;
                font-weight: 700;
                color: #111827;
              }
              .qr-date-picker-modal .rdp-nav {
                position: absolute;
                top: 0;
                display: flex;
                align-items: center;
                gap: 4px;
              }
              .qr-date-picker-modal .rdp-nav_button {
                width: 32px;
                height: 32px;
                border-radius: 8px;
                background-color: transparent;
                border: none;
                cursor: pointer;
                transition: background-color 0.2s;
                color: #11346A;
                font-size: 18px;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .qr-date-picker-modal .rdp-nav_button:hover {
                background-color: #f3f4f6;
              }
              .qr-date-picker-modal .rdp-head_cell {
                font-size: 13px;
                font-weight: 600;
                color: #6b7280;
                padding: 8px 0;
                text-transform: none;
              }
              .qr-date-picker-modal .rdp-cell {
                padding: 2px;
              }
              .qr-date-picker-modal .rdp-button {
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                transition: all 0.2s;
                border: 1px solid transparent;
              }
              .qr-date-picker-modal .rdp-day {
                color: #374151;
              }
              .qr-date-picker-modal .rdp-day:hover:not(.rdp-day_selected) {
                background-color: #f3f4f6;
                border-color: #d1d5db;
              }
              .qr-date-picker-modal .rdp-day_selected {
                background-color: #11346A !important;
                color: white !important;
                font-weight: 600;
                border-color: #11346A;
              }
              .qr-date-picker-modal .rdp-day_today:not(.rdp-day_selected) {
                font-weight: 700;
                color: #11346A;
                background-color: #e8f0ff;
              }
              .qr-date-picker-modal .rdp-day_outside {
                color: #d1d5db;
              }
            `}</style>
            <div style={{
              display: 'flex',
              gap: '24px',
              alignItems: 'flex-start'
            }}>
              <div className="qr-date-picker-modal">
                <DayPicker
                  mode="single"
                  selected={classDate}
                  onSelect={(date) => {
                    if (date) {
                      const [hours, minutes] = classTime.split(':');
                      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                      setClassDate(date);
                    }
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                paddingLeft: '24px',
                borderLeft: '1px solid #e5e7eb',
                minWidth: '180px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                    선택된 날짜
                  </label>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#11346A'
                  }}>
                    {format(classDate, 'yyyy년 MM월 dd일')}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280' }}>
                    수업 시간
                  </label>
                  <input
                    type="time"
                    value={classTime}
                    onChange={(e) => {
                      setClassTime(e.target.value);
                      const [hours, minutes] = e.target.value.split(':');
                      const newDate = new Date(classDate);
                      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                      setClassDate(newDate);
                    }}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#11346A',
                      backgroundColor: '#fff',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            color: ACCENT_COLOR,
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* 스캔 버튼 */}
        <button
          onClick={handleScan}
          disabled={!selectedFile || scanning}
          style={{
            width: '100%',
            padding: '14px',
            fontSize: '16px',
            fontWeight: 'bold',
            borderRadius: '8px',
            backgroundColor: scanning || !selectedFile ? '#ccc' : BRAND_COLOR,
            border: 'none',
            color: '#FFF',
            cursor: scanning || !selectedFile ? 'not-allowed' : 'pointer',
            marginBottom: '20px'
          }}
        >
          {scanning ? `스캔 중... ${scanProgress}%` : 'QR 스캔 시작'}
        </button>

        {/* 스캔 진행 상태 */}
        {scanning && (
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${scanProgress}%`,
                height: '100%',
                backgroundColor: BRAND_COLOR,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        )}

        {renderScanResultsSection()}

        {/* 제출 진행 상태 */}
        {submitting && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666', fontWeight: '500' }}>
              제출 중... ({submitProgress.current}/{submitProgress.total})
            </div>
            <div style={{
              width: '100%',
              height: '8px',
              backgroundColor: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(submitProgress.current / submitProgress.total) * 100}%`,
                height: '100%',
                backgroundColor: BRAND_COLOR,
                transition: 'width 0.3s'
              }} />
            </div>
          </div>
        )}

        {/* Force Send Option */}
        <div style={{
          marginTop: '20px',
          marginBottom: '15px',
          padding: '12px',
          backgroundColor: '#fff8dc',
          border: '1px solid #ffd700',
          borderRadius: '5px'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#333'
          }}>
            <input
              type="checkbox"
              checked={forceSend}
              onChange={(e) => setForceSend(e.target.checked)}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                width: '16px',
                height: '16px'
              }}
            />
            <span>
              즉시 발송 (대기열 무시)
              <span style={{
                marginLeft: '8px',
                fontSize: '12px',
                color: '#666',
                fontWeight: 'normal'
              }}>
                오후 11시 이후에도 즉시 발송합니다
              </span>
            </span>
          </label>
        </div>

        {/* 제출 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
          <button
            onClick={handleClose}
            disabled={submitting}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              borderRadius: '8px',
              backgroundColor: '#FFF',
              border: '2px solid #e0e0e0',
              color: '#666',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              opacity: submitting ? 0.5 : 1
            }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '14px 28px',
              fontSize: '15px',
              borderRadius: '8px',
              backgroundColor: submitting ? '#ccc' : BRAND_COLOR,
              border: 'none',
              color: '#FFF',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              boxShadow: submitting ? 'none' : '0 4px 12px rgba(17, 52, 106, 0.2)'
            }}
          >
            {submitting ? '제출 중...' : '제출하기'}
          </button>
        </div>
      </div>
      </div>
    </>
  );
};

export default QRBulkUploadModal;
